import {
  extractFoliationSpans,
  extractWordSpans,
  getSegmentableWordData,
  normalizeSplitIndices,
} from './manualStudioSegmentationResult'
import type { WorkbenchPropagationRowAssignment } from './workbenchPropagation'

export interface TranslationCharAssignment {
  rowId: string
  status: 'mapped' | 'gap' | 'uncertain' | 'transposed'
  spanText: string
  startChar: number | null
  endChar: number | null
  confidence?: string
  notes?: string
}

export interface DerivedTranslationPropagationPlan {
  splitIndices: number[]
  rowAssignments: WorkbenchPropagationRowAssignment[]
  mappedRowCount: number
  fragmentDiagnostics: TranslationFragmentDiagnostics
}

interface TokenRangeAssignment {
  rowId: string
  startTokenIndex: number
  endTokenIndex: number
}

export interface TranslationFragmentDiagnosticSegment {
  kind: 'extra_unclaimed_text'
  startTokenIndex: number
  endTokenIndex: number
  text: string
}

export interface TranslationFragmentDiagnostics {
  omittedRowIds: string[]
  droppedAssignmentRowIds: string[]
  transposedRowIds: string[]
  unclaimedSegments: TranslationFragmentDiagnosticSegment[]
}

export const summarizeTranslationFragmentDiagnostics = (
  diagnostics: TranslationFragmentDiagnostics,
): string | null => {
  const parts: string[] = []
  if (diagnostics.omittedRowIds.length > 0) {
    parts.push(`${diagnostics.omittedRowIds.length} omitted row${diagnostics.omittedRowIds.length === 1 ? '' : 's'}`)
  }
  if (diagnostics.droppedAssignmentRowIds.length > 0) {
    parts.push(`${diagnostics.droppedAssignmentRowIds.length} dropped span${diagnostics.droppedAssignmentRowIds.length === 1 ? '' : 's'}`)
  }
  if (diagnostics.unclaimedSegments.length > 0) {
    parts.push(`${diagnostics.unclaimedSegments.length} extra fragment${diagnostics.unclaimedSegments.length === 1 ? '' : 's'}`)
  }
  if (diagnostics.transposedRowIds.length > 0) {
    parts.push(`${diagnostics.transposedRowIds.length} transposed row${diagnostics.transposedRowIds.length === 1 ? '' : 's'}`)
  }
  return parts.length > 0 ? `Coverage diagnostics: ${parts.join(' · ')}` : null
}

const mapCharAssignmentToTokenRange = (
  segmentableWordSpans: ReturnType<typeof getSegmentableWordData>['segmentableWordSpans'],
  assignment: TranslationCharAssignment,
  minimumStartTokenIndex: number,
): TokenRangeAssignment | null => {
  const startChar = assignment.startChar
  const endChar = assignment.endChar
  if (
    assignment.status === 'gap'
    || typeof startChar !== 'number'
    || typeof endChar !== 'number'
    // mutation-ignore-next-line equivalent: reversed spans fail the token-overlap checks below too.
    || endChar <= startChar
  ) {
    return null
  }

  let low = 0
  let high = segmentableWordSpans.length
  while (low < high) {
    const middle = Math.floor((low + high) / 2)
    if (segmentableWordSpans[middle].end <= startChar) {
      low = middle + 1
    } else {
      high = middle
    }
  }
  const startTokenIndex = low

  low = startTokenIndex
  high = segmentableWordSpans.length
  while (low < high) {
    const middle = Math.floor((low + high) / 2)
    if (segmentableWordSpans[middle].start < endChar) {
      low = middle + 1
    } else {
      high = middle
    }
  }
  const endTokenIndex = low

  if (
    startTokenIndex >= segmentableWordSpans.length
    || segmentableWordSpans[startTokenIndex].start >= endChar
  ) {
    return null
  }
  // mutation-ignore-next-line equivalent: zero-width token ranges cannot be produced by span overlap.
  if (endTokenIndex <= startTokenIndex) {
    return null
  }

  const clampedStartTokenIndex = Math.max(startTokenIndex, minimumStartTokenIndex)
  if (endTokenIndex <= clampedStartTokenIndex) {
    return null
  }

  return {
    rowId: assignment.rowId,
    startTokenIndex: clampedStartTokenIndex,
    endTokenIndex,
  }
}

export const deriveTranslationPropagationPlan = ({
  text,
  charAssignments,
}: {
  text: string
  charAssignments: TranslationCharAssignment[]
}): DerivedTranslationPropagationPlan => {
  const wordSpans = extractWordSpans(text)
  const foliationSpans = extractFoliationSpans(text, wordSpans)
  const { segmentableWordSpans } = getSegmentableWordData(wordSpans, foliationSpans)
  if (segmentableWordSpans.length === 0) {
    return {
      splitIndices: [],
      rowAssignments: charAssignments.map(assignment => ({
        rowId: assignment.rowId,
        segmentIndex: null,
        translationStatus: assignment.status,
      })),
      mappedRowCount: 0,
      fragmentDiagnostics: {
        omittedRowIds: charAssignments
          .filter(assignment => assignment.status === 'gap')
          .map(assignment => assignment.rowId),
        droppedAssignmentRowIds: charAssignments
          .filter(assignment => assignment.status !== 'gap')
          .map(assignment => assignment.rowId),
        transposedRowIds: charAssignments
          .filter(assignment => assignment.status === 'transposed')
          .map(assignment => assignment.rowId),
        unclaimedSegments: [],
      },
    }
  }

  const mappedRanges: TokenRangeAssignment[] = []
  const droppedAssignmentRowIds: string[] = []
  let cursor = 0
  charAssignments.forEach(assignment => {
    const range = mapCharAssignmentToTokenRange(segmentableWordSpans, assignment, cursor)
    if (!range) {
      if (assignment.status !== 'gap') {
        droppedAssignmentRowIds.push(assignment.rowId)
      }
      return
    }
    mappedRanges.push(range)
    cursor = range.endTokenIndex
  })

  const segments: Array<{ startTokenIndex: number; endTokenIndex: number; rowId?: string }> = []
  const unclaimedSegments: TranslationFragmentDiagnosticSegment[] = []
  let tokenCursor = 0
  mappedRanges.forEach(range => {
    if (tokenCursor < range.startTokenIndex) {
      const segment = {
        startTokenIndex: tokenCursor,
        endTokenIndex: range.startTokenIndex,
      }
      segments.push(segment)
      unclaimedSegments.push({
        kind: 'extra_unclaimed_text',
        startTokenIndex: segment.startTokenIndex,
        endTokenIndex: segment.endTokenIndex,
        text: segmentableWordSpans
          .slice(segment.startTokenIndex, segment.endTokenIndex)
          .map(span => text.slice(span.start, span.end))
          .join(' ')
          .trim(),
      })
    }
    segments.push(range)
    tokenCursor = range.endTokenIndex
  })
  if (tokenCursor < segmentableWordSpans.length) {
    const segment = {
      startTokenIndex: tokenCursor,
      endTokenIndex: segmentableWordSpans.length,
    }
    segments.push(segment)
    unclaimedSegments.push({
      kind: 'extra_unclaimed_text',
      startTokenIndex: segment.startTokenIndex,
      endTokenIndex: segment.endTokenIndex,
      text: segmentableWordSpans
        .slice(segment.startTokenIndex, segment.endTokenIndex)
        .map(span => text.slice(span.start, span.end))
        .join(' ')
        .trim(),
    })
  }

  const splitIndices = normalizeSplitIndices(
    segments
      .slice(0, -1)
      .map(segment => segment.endTokenIndex)
      .filter(index => index > 0),
    segmentableWordSpans.length,
  )

  const segmentIndexByRowId = new Map<string, number>()
  const mappedRangeByRowId = new Map<string, TokenRangeAssignment>()
  segments.forEach((segment, segmentIndex) => {
    if (segment.rowId) {
      segmentIndexByRowId.set(segment.rowId, segmentIndex)
    }
  })
  mappedRanges.forEach(range => mappedRangeByRowId.set(range.rowId, range))

  return {
    splitIndices,
    rowAssignments: charAssignments.map(assignment => {
      const mappedRange = mappedRangeByRowId.get(assignment.rowId)
      return {
        rowId: assignment.rowId,
        segmentIndex: segmentIndexByRowId.has(assignment.rowId)
          ? segmentIndexByRowId.get(assignment.rowId) ?? null
          : null,
        translationStatus: assignment.status,
        ...(mappedRange
          ? {
              startTokenIndex: mappedRange.startTokenIndex,
              endTokenIndex: mappedRange.endTokenIndex - 1,
            }
          : {}),
      }
    }),
    mappedRowCount: mappedRanges.length,
    fragmentDiagnostics: {
      omittedRowIds: charAssignments
        .filter(assignment => assignment.status === 'gap')
        .map(assignment => assignment.rowId),
      droppedAssignmentRowIds,
      transposedRowIds: charAssignments
        .filter(assignment => assignment.status === 'transposed')
        .map(assignment => assignment.rowId),
      unclaimedSegments,
    },
  }
}
